<template>
    <ElementsProductCard
        v-if="showNewProductCardVisual"
        :show-description="showDescription"
        :product="item"
        :lazy-image="lazyImage"
        :list-position="listPosition"
        :list-title="listTitle"
        :list-type="listType"
        :item-list-name="itemListName"
        :is-carousel="isCarousel"
        :carousel-name="carouselName"
        :show-add-to-basket="showAddToBasket"
        :show-pre-basket="showPreBasket"
        :show-availability="showAvailability"
        :addon-sale-behavior="addonSaleBehavior"
        :custom-button-title="customButtonTitle"
        @custom-button-clicked="$emit('customButtonClicked', $event)"
        @product-redirected-to-url="redirectToUrl"
        @product-added-to-basket="(productUrl: string) => $emit('productAddedToBasket', productUrl)"
    />
    <div v-else :class="{ 'product--highlight': highlight, 'product--minimalized': minimalizedProductVisual }" class="product">
        <ObjectsProductImage
            :product="item"
            :lazy-image="lazyImage"
            :is-carousel="isCarousel"
            :show-rating="!minimalizedProductVisual"
            :small="minimalizedProductVisual"
            :show-rating-pop-up="showRatingPopUp"
            @redirect-to-url="redirectToUrl"
        />

        <div class="product__content">
            <ObjectsProductName :product="item" :lines="minimalizedProductVisual ? 2 : 3" @redirect-to-url="redirectToUrl" />

            <ObjectsProductAvailability v-if="showAvailability" :product="item" :has-variants="hasVariants" />

            <div v-if="item.isAvailable || hasVariants" class="product__price_wrapper notranslate">
                <UiPriceInPharmacy v-if="showPharmacyPrice && item.pharmacyDealPrice" :price="item.pharmacyDealPrice || 0" :size="'xl'" />
                <UiSuperPrice
                    v-if="!hasVariants && item.hasSuperPrice"
                    :price-before="item.priceBeforeDiscount || 0"
                    :price-actual="item.price"
                    :lowest-price="item.lowestPrice"
                    :title="false"
                />
                <CompetitorSuperPrice
                    v-else-if="!hasVariants && item.hasCompetitorSuperPrice"
                    :show-add-button="item.isAvailable && !hasVariants"
                    :title="false"
                    :product="item"
                />
                <ObjectsProductPrices v-else :product="item" :addon-sale-behavior="addonSaleBehavior" @redirect-to-url="redirectToUrl" />
                <div v-if="showComparablePrice" class="price-perunit" v-html="item.attributes.comparablePrice"></div>
            </div>

            <a v-if="!item.isAvailable && !hasVariants" :href="item.url" class="product__detail-btn btn btn-outline-primary">{{
                $t('detail')
            }}</a>
            <TheButton v-if="customButtonTitle" :title="customButtonTitle" @click="$emit('customButtonClicked', item.id)" />
            <template v-else-if="showAddToBasket">
                <ElementsButtonAddBasket
                    v-if="item.isAvailable && !hasVariants"
                    ref="addToBasketButton"
                    :add-basket-align="``"
                    :button-class="`btn-outline-primary add-cart__simple`"
                    :product="item"
                    :product-id="item.id"
                    :product-price="item.price"
                    :is-preorder="item.isPreorder"
                    :max-amount-per-order="item.attributes.maxAmountPerOrder"
                    :has-substitution-modal="item.substitutions?.isModal || false"
                    :list-position="listPosition"
                    :list-type="listType"
                    :item-list-name="itemListName"
                    :carousel-name="carouselName"
                    :list-title="listTitle"
                    :show-pre-basket="showPreBasket"
                    show-actual-state-mode="small"
                    :addon-sale-behavior="addonSaleBehavior"
                    @product-added-to-basket="(productUrl: string) => $emit('productAddedToBasket', productUrl)"
                />
                <ObjectsProductVariantsLink v-else-if="hasVariants" :product="item" @redirect-to-url="redirectToUrl" />
            </template>
            <div v-if="showMigrateToAppButton && !addonSaleBehavior" class="migrate-to-app__wrapper">
                <TheButton :title="mobileActionTitle" class="migrate-to-app__button" @click="openMigrateToMobileAppModal" />
            </div>

            <ObjectsProductGiftActions :product="item" />

            <ObjectsProductExpiration v-if="item.expirationDate" :product="item" />
        </div>
    </div>
</template>

<script lang="ts">
import Modal from '~/components/Modal.vue'
import Product from '~/models/Product/Product'
import DataLayer from '~/extensions/DataLayer'
import GtmEcommerceEvent from '~/models/DataLayer/GtmEcommerceEvent'
import { GtmEcommerceEventTypeEnum } from '~/models/DataLayer/GtmEcommerceEventTypeEnum'
import CompetitorSuperPrice from '~/components/elements/CompetitorSuperPrice.vue'
import TheButton from '~/components/ui/TheButton.vue'
import { transformProductToGtmProduct } from '~/utils/transformProductToGtmProduct'
import { useAbTestStore } from '~/stores/abTest'
import useProductDetailAsyncData from '~/composables/productDetail/useProductDetailAsyncData'

export default defineNuxtComponent({
    components: {
        TheButton,
        Modal,
        CompetitorSuperPrice,
    },

    props: {
        item: {
            type: Object as PropType<Product>,
            default: () => ({}),
        },

        highlight: {
            type: Boolean,
            default: false,
        },

        lazyImage: {
            type: Boolean,
            default: true,
        },

        showDescription: {
            type: Boolean,
            default: false,
        },

        listPosition: {
            type: Number,
            default: null,
        },

        listTitle: {
            type: String,
            default: '',
        },

        listType: {
            type: String,
            default: '',
        },

        itemListName: {
            type: String,
            default: '',
        },

        carouselName: {
            type: String,
            default: '',
        },

        isCarousel: {
            type: Boolean,
            default: false,
        },

        showAddToBasket: {
            type: Boolean,
            default: true,
        },

        customButtonTitle: {
            type: String,
            default: null,
            required: false,
        },

        minimalizedProductVisual: {
            type: Boolean,
            default: false,
        },

        showPreBasket: {
            type: Boolean,
            default: true,
        },

        showAvailability: {
            type: Boolean,
            default: true,
        },

        addonSaleBehavior: {
            type: Boolean,
            default: false,
        },

        showPharmacyPrice: {
            type: Boolean,
            default: true,
        },

        showRatingPopUp: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            rating: 5,
            ratingShowStar: 3,
            visible: false,
            modalDeliveryVisible: false,
            modalName: `modal-deliveries-${this.item.id}-${Math.random()}`,
        }
    },

    methods: {
        openDeliveryModal(): void {
            if (!this.item.availabilityModalEnabled) {
                return
            }

            useNuxtApp().$bus.$emit('showModal', this.modalName)
            this.modalDeliveryVisible = true
        },

        rateChange(rate: number): void {
            this.rating = rate
        },

        async redirectToUrl(event: Event): Promise<void> {
            useProductDetailAsyncData(this.item.id)

            setTimeout(() => {
                // do long task in new thread
                const { listPosition, listTitle, listType, itemListName, carouselName } = this

                const ecommerceEvent = {
                    type: GtmEcommerceEventTypeEnum.ClickToProductDetail,
                    products: [transformProductToGtmProduct(this.item)],
                    targetListPosition: listPosition,
                    listName: DataLayer.getListName(listTitle, listType),
                    itemListName,
                    carouselName,
                } as GtmEcommerceEvent

                DataLayer.pushEcommerceEventToDataLayer(ecommerceEvent)

                this.$emit('productRedirectedToUrl', this.item.url)
            }, 0)

            const href = (event.currentTarget as HTMLAnchorElement)?.getAttribute('href') || ''
            const external = href.startsWith('http')
            await safeNavigateTo(href, { external })
        },

        openMigrateToMobileAppModal() {
            useNuxtApp().$bus.$emit('showMigrateToMobileAppModal', this.item)
        },
    },
    computed: {
        showNewProductCardVisual(): boolean {
            const abTestStore = useAbTestStore()
            const variant = abTestStore.getVariant('ProductCardAbTest')

            return variant === null || variant === 2
        },

        hasVariants(): boolean {
            // eslint-disable-next-line no-prototype-builtins
            return this.item.hasOwnProperty('variantsCount') && this.item.variantsCount > 0
        },

        mobileActionTitle(): string {
            if (this.item.mobileAppPrice || this.item.mobileAppPriceForLoggedInUser) {
                return this.$t('basket.migration.ctaPrice', {
                    price: this.$formatCurrency(this.item.mobileAppPrice || this.item.mobileAppPriceForLoggedInUser),
                })
            }

            return this.$t('basket.migration.cta')
        },

        showMigrateToAppButton(): boolean {
            const config = useRuntimeConfig()

            return (
                config.public.migrateToMobileApp &&
                (this.item.mobileAppPrice || this.item.mobileAppPriceForLoggedInUser || this.item.hasMobileAppAction)
            )
        },

        showComparablePrice(): boolean {
            if (this.listType === 'search') {
                return this.item.attributes.hasCategoryComparablePriceSort && this.item.attributes.comparablePrice
            }

            return this.item.attributes.comparablePrice
        },
    },
})
</script>

<style lang="scss" scoped>
@import '../../node_modules/bootstrap/scss/functions';
@import '../../node_modules/bootstrap/scss/variables';
@import '../../node_modules/bootstrap/scss/mixins';

.product {
    display: flex;
    flex-direction: column;
    position: relative;
    width: 100%;
    font-size: 14px;
    padding: 1.5em 0.5em 0.5em;
    transition: 150ms ease-in-out;
    border-radius: 10px;
    text-align: center;
    margin: 0 6px 0;
    background: #fff;
    // content-visibility: auto; => causes CLS problem :(

    &__detail-btn {
        font-size: 14px;
        min-width: 120px;
        font-weight: 500;
        white-space: nowrap;
        width: 100%;
        max-width: 140px;
        padding: 3px 20px 5px;
        margin: 13px auto 0;
    }

    .counter {
        display: none !important;
    }

    .add-cart__container {
        text-align: center;
        margin-top: 5px;
    }

    .cover-link {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }

    &:hover {
        @media (min-width: 1024px) {
            box-shadow: 0 1px 6px rgb(148 170 179 / 30%);
            transition: 150ms ease-in-out;
        }

        img {
            @media (min-width: 1024px) {
                transform: scale(1.05);
                transition: 150ms ease-in-out;
            }
        }

        .product__name h3 {
            @media (min-width: 1024px) {
                color: $color-cta;
            }
        }
    }

    &--no-shadow {
        border: 1px solid $color-white;
        padding: 1.5em 0 0.5em;
        margin: 0;

        &:hover {
            cursor: pointer;
            transition: none;
            box-shadow: none;
            border: 1px solid $color-grey;
        }
    }

    &__content {
        margin-top: 20px; //place for stars
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
    }

    .add-cart {
        width: 100%;
        max-width: 160px;
        margin: 0 auto;
    }

    .add-cart__change {
        border-radius: 50px;
        width: 31px;
        min-width: 31px;
        height: 31px;
        line-height: 26px;
        display: flex;
        align-items: flex-start;
        justify-content: center;
    }

    .btn-minus {
        color: $color-cta;
    }

    &__price_wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        gap: 4px;
        margin-bottom: 4px;
    }
}

.product.product--minimalized {
    .product {
        &__content {
            margin-top: 0;
        }

        &__name {
            height: 40px;

            h3 {
                -webkit-line-clamp: 2;
            }
        }
    }
}

.product.product--highlight {
    margin: 0;
    flex-direction: row;
    justify-content: space-between;
    padding-top: 0;

    @include screen-md {
        padding: 1.5em 1em 2.5em 1em;
    }

    &:hover {
        box-shadow: none;
    }

    .product__image {
        width: 40%;
        margin-bottom: 0;
    }

    .product__content {
        margin-top: 0;
        width: 55%;
        text-align: left;
    }

    &:deep(.alert-addcart) {
        //  width: calc(40% - 10px);
    }

    .product__name {
        padding: 0;
        margin: 0 0 10px 0;
        align-items: flex-start;
        font-weight: 600;
        font-size: 14px;
        line-height: 19px;
        height: 38px;
        color: $color-dark;
        justify-content: flex-start;

        h3 {
            -webkit-line-clamp: 2;
            line-height: 19px;
            height: 38px;
            font-weight: 600;
            color: $color-dark;
        }
    }

    .product__price {
        justify-content: flex-start;
    }

    .add-cart__container {
        text-align: left;
        margin-top: 5px;
    }

    .product__gift {
        padding-left: 0;
        padding-right: 0;
    }

    .product__price {
        @media only screen and (max-width: 767px) {
            font-size: 14px;
        }

        .old {
            @media only screen and (max-width: 767px) {
                font-size: 12px;
            }
        }
    }

    .product-discount .discount {
        width: 38px;
        height: 38px;

        @media only screen and (min-width: 768px) {
            width: 40px;
            height: 40px;
        }
    }

    &:deep(.add-cart),
    &:deep(.add-cart__simple) {
        @media only screen and (max-width: 767px) {
            min-width: 126px;
            max-width: 126px;
        }
    }

    &:deep(.add-cart__simple) {
        @media only screen and (max-width: 767px) {
            font-size: 12px;
        }
    }
}

@include screen-md {
    .product.product--oneItem {
        .product__name h3 {
            font-size: 16px;
        }
        .product__bottom {
            display: flex;
            justify-content: space-between;
            gap: 10px;
        }
        .product__price {
            margin-bottom: 0;
        }
    }
}

.migrate-to-app {
    &__wrapper {
        margin: 0.5rem auto;
    }

    &__button {
        font-size: 14px;
        font-weight: 500;
        min-width: 160px;
        white-space: nowrap;
        width: 100%;
        padding: 3px 20px 5px;
    }
}

.price-perunit {
    color: $color-grey-extra-dark;
    font-size: 12px;
}
</style>
